import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  fetchProfile,
  fetchBadgeCatalog,
  getActivePlayer,
  setActivePlayer,
  GAME_BADGE_META,
} from "@/utils/playerProfile";

const BadgeProfile = ({ isOpen, onClose }) => {
  const [name, setName] = useState(() => getActivePlayer());
  const [profile, setProfile] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAll = useCallback(async (playerName) => {
    if (!playerName?.trim()) { setProfile(null); return; }
    setLoading(true);
    const [p, c] = await Promise.all([fetchProfile(playerName), fetchBadgeCatalog()]);
    setProfile(p);
    setCatalog(c);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const cur = getActivePlayer();
      setName(cur);
      loadAll(cur);
    }
  }, [isOpen, loadAll]);

  const handleSaveName = () => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    setActivePlayer(trimmed);
    loadAll(trimmed);
  };

  if (!isOpen) return null;

  const earnedSet = new Set(profile?.badges || []);
  const games = catalog?.games || Object.keys(GAME_BADGE_META);
  const specials = catalog?.specials || [];
  const totalEarned = profile?.badges?.length || 0;
  const totalAvailable = games.length + specials.length;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="badge-profile-overlay"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
          className="glass-card-strong rounded-3xl p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-gold/30"
          onClick={(e) => e.stopPropagation()}
          data-testid="badge-profile-dialog"
        >
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-fredoka text-2xl text-gold font-bold flex items-center gap-2">
              <Award className="w-6 h-6" />Profil Badge
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-gold hover:bg-gold/10 rounded-full" data-testid="badge-profile-close">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Name input */}
          <div className="glass-card rounded-xl p-3 mb-4">
            <label className="font-fredoka text-gold-light/80 text-xs mb-1 flex items-center gap-1">
              <User className="w-3 h-3" />Nama Pemain
            </label>
            <div className="flex gap-2">
              <input
                data-testid="badge-profile-name-input"
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                placeholder="Masukkan nama..." maxLength={30}
                className="flex-1 glass-card border-gold/20 rounded-lg px-3 py-1.5 text-white font-fredoka text-sm focus:outline-none"
              />
              <Button data-testid="badge-profile-save-name" onClick={handleSaveName}
                className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-fredoka font-bold rounded-lg h-9 px-4 text-sm">
                Simpan
              </Button>
            </div>
          </div>

          {/* Stats */}
          {profile && (
            <>
              <div className="grid grid-cols-4 gap-2 mb-3 text-center" data-testid="badge-profile-stats">
                <div className="glass-card rounded-xl p-2"><div className="font-fredoka text-2xl text-gold font-bold">{totalEarned}</div><div className="font-fredoka text-xs text-gold-light/60">Badge</div></div>
                <div className="glass-card rounded-xl p-2"><div className="font-fredoka text-2xl text-gold font-bold">{profile.games_played_count}</div><div className="font-fredoka text-xs text-gold-light/60">Game</div></div>
                <div className="glass-card rounded-xl p-2"><div className="font-fredoka text-2xl text-gold font-bold">{profile.total_wins}</div><div className="font-fredoka text-xs text-gold-light/60">Menang</div></div>
                <div className="glass-card rounded-xl p-2"><div className="font-fredoka text-2xl text-orange-400 font-bold">🔥{profile.streak || 0}</div><div className="font-fredoka text-xs text-gold-light/60">Hari</div></div>
              </div>
              {/* Daily Challenge banner */}
              <div className={`rounded-xl p-3 mb-4 border-2 ${profile.daily_completed ? "bg-green-500/20 border-green-400/40" : "bg-orange-500/15 border-orange-400/30"}`} data-testid="badge-daily-challenge">
                <div className="flex items-center justify-between">
                  <span className="font-fredoka text-base">
                    {profile.daily_completed ? "✅ Tantangan harian selesai!" : "🎯 Tantangan Harian"}
                  </span>
                  <span className="font-fredoka text-xs text-white/70">Main 3 game beda</span>
                </div>
                {!profile.daily_completed && (
                  <p className="font-fredoka text-xs text-white/60 mt-1">
                    Selesaikan untuk jaga streak {profile.streak || 0}-hari kamu!
                  </p>
                )}
              </div>
            </>
          )}

          {loading && <div className="text-center font-fredoka text-gold-light/60 py-6">Memuat...</div>}

          {!loading && !profile && (
            <div className="text-center font-fredoka text-gold-light/60 py-6">
              Masukkan nama untuk melihat badge.
            </div>
          )}

          {!loading && profile && (
            <>
              <h3 className="font-fredoka text-gold font-bold mb-2 text-sm">🎮 Badge Game ({games.filter(g => earnedSet.has(g)).length}/{games.length})</h3>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4" data-testid="badge-game-grid">
                {games.map((gid) => {
                  const meta = GAME_BADGE_META[gid] || { name: gid, icon: "🎮" };
                  const earned = earnedSet.has(gid);
                  return (
                    <div key={gid} data-testid={`badge-${gid}`}
                      className={`flex flex-col items-center p-2 rounded-xl border-2 ${earned ? "bg-gradient-to-br from-yellow-400/20 to-amber-600/20 border-gold/60" : "glass-card border-gold/10 opacity-40 grayscale"}`}>
                      <span className="text-2xl mb-0.5">{meta.icon}</span>
                      <span className="font-fredoka text-[10px] text-center font-bold text-gold-light leading-tight">{meta.name}</span>
                      {earned && <span className="text-[10px] text-yellow-400">✓</span>}
                    </div>
                  );
                })}
              </div>

              <h3 className="font-fredoka text-gold font-bold mb-2 text-sm">⭐ Badge Spesial ({specials.filter(s => earnedSet.has(s.id)).length}/{specials.length})</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2" data-testid="badge-special-grid">
                {specials.map(sb => {
                  const earned = earnedSet.has(sb.id);
                  return (
                    <div key={sb.id} data-testid={`badge-special-${sb.id}`}
                      className={`flex flex-col items-center p-2 rounded-xl border-2 ${earned ? "bg-gradient-to-br from-purple-400/30 to-pink-600/30 border-gold animate-pulse" : "glass-card border-gold/10 opacity-40 grayscale"}`}>
                      <span className="text-3xl mb-1">{sb.icon}</span>
                      <span className="font-fredoka text-[10px] text-center font-bold text-gold-light leading-tight">{sb.name}</span>
                      <span className="font-fredoka text-[8px] text-center text-gold-light/50 leading-tight">{sb.desc}</span>
                    </div>
                  );
                })}
              </div>

              <p className="font-fredoka text-xs text-gold-light/40 text-center mt-3">
                Total: <span className="text-gold font-bold">{totalEarned}/{totalAvailable}</span> badge
              </p>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BadgeProfile;
